import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import first from 'lodash/first';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import {
  addDays, endOfDay, startOfDay, formatISO,
} from 'date-fns';

import { DEFAULT_TIME_ZONE_NAME } from '@shared/constants';
import { EXAM_CENTERS_CONFIG } from '../../constants';
import stores from '../../stores';
import { BookingScreens } from './BookingStates';
import { Header } from './Header';
import { SCREENS } from './Screens';
import { LICENSE_TYPES_DATA } from '../../constants/LicenseTypes';

const {
  BOOKING_ERROR, REGISTRATION_TABS, SLOTS, USER_INFO, LICENSE_TYPE,
} = BookingScreens;

class BookingFormClassComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentSlot: null,
      selectedLicenseType: null,
      calendarErrorMessage: '',
    };
  }

  componentDidMount = () => {
    stores.userStore.getUser();
    this.loadBookingSessions();
  };

  loadBookingSessions = (startDate, endDate) => {
    const { examCenter, examCenterSiteId, examCenterTimezone } = this.props;
    const examCenterType = EXAM_CENTERS_CONFIG[examCenter];
    const today = startOfDay(new Date());
    const fromNow = startDate || today.toISOString();
    const toDate = endDate || endOfDay(addDays(today, 20)).toISOString();

    stores.bookingStore.getBookingSessions(
      examCenterType.name,
      examCenterSiteId,
      fromNow,
      toDate,
      7,
      examCenterTimezone,
    );
  };

  onSlotClickHandler = (slot) => {
    this.setState({
      currentSlot: slot,
      calendarErrorMessage: '',
    });

    stores.bookingStore.activeScreen = LICENSE_TYPE;
  };

  onClickResetSlot = () => {
    this.setState({
      currentSlot: null,
    });

    stores.bookingStore.activeScreen = SLOTS;
  };

  onClickNavLeftHandler = () => {
    const { dateCaches } = stores.bookingStore;
    const previousDateRange = dateCaches[dateCaches.length - 2];
    stores.bookingStore.dropLastDateFromCache(2);

    const firstPrevDateRangeItem = first(previousDateRange);
    const lastPrevDateRangeItem = last(previousDateRange);
    const firstPrevDateRangeItemDateFormat = new Date(firstPrevDateRangeItem);
    const lastPrevDateRangeItemDateFormat = new Date(lastPrevDateRangeItem);

    const fromDate = formatISO(startOfDay(firstPrevDateRangeItemDateFormat));
    const toDate = formatISO(endOfDay(lastPrevDateRangeItemDateFormat));

    this.loadBookingSessions(fromDate, toDate);
  };

  onClickNavRightHandler = () => {
    const { dateCaches } = stores.bookingStore;
    const lastDate = last(last(dateCaches));
    const lastDateToDateFormat = new Date(lastDate);

    const fromDate = formatISO(startOfDay(addDays(lastDateToDateFormat, 1)));
    const toDate = formatISO(endOfDay(addDays(lastDateToDateFormat, 21)));

    this.loadBookingSessions(fromDate, toDate);
  };

  onExamSigninFormSubmitHandler = async (data, formikBag) => {
    try {
      await stores.userStore.signIn(data, formikBag);
      this.bookSlot();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  onExamRegFormSubmitHandler = async (data, formikBag) => {
    try {
      await stores.userStore.signUp(data, formikBag);
      this.bookSlot();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  bookSlot = () => {
    const { currentSlot, selectedLicenseType } = this.state;
    const { examCenter } = this.props;
    const examCenterType = EXAM_CENTERS_CONFIG[examCenter];

    if (!currentSlot) {
      this.setState({
        calendarErrorMessage: "Merci de sélectionner une session d'examen de code.",
      });
      return false;
    }

    const data = {
      sessionId: currentSlot.session_id,
      siteId: currentSlot.site_id,
      companyName: examCenterType.name,
      license_category: LICENSE_TYPES_DATA[selectedLicenseType].category,
    };

    return stores.bookingStore.makeBooking(data);
  };

  onTypeClick = (e) => {
    this.setState({
      selectedLicenseType: e.target.value,
    });

    const { userInfo } = stores.userStore;
    stores.bookingStore.activeScreen = isEmpty(userInfo) ? REGISTRATION_TABS : USER_INFO;
  };

  onEditLicenseClick = () => {
    stores.bookingStore.activeScreen = LICENSE_TYPE;
  };

  render() {
    const {
      dateCaches, bookingSessions, activeScreen, errorText,
    } = stores.bookingStore;
    const {
      examCenter, averageStars, reviewsCount, examCenterTimezone, examCenterSiteId,
    } = this.props;
    const { currentSlot, calendarErrorMessage, selectedLicenseType } = this.state;
    const isLeftNavVisible = !isEmpty(dateCaches) && dateCaches.length !== 1;
    const isRightNavVisible = !isEmpty(bookingSessions);
    const Screen = SCREENS[activeScreen];

    const getScreenProps = () => {
      const SCREENS_PROPS = {
        [SLOTS]: {
          examCenterTimezone,
          slots: bookingSessions,
          onSlotClick: this.onSlotClickHandler,
          onClickNavLeft: this.onClickNavLeftHandler,
          isLeftNavVisible,
          onClickNavRight: this.onClickNavRightHandler,
          isRightNavVisible,
          errorMessage: calendarErrorMessage,
          examCenter,
          examCenterSiteId,
        },
        [LICENSE_TYPE]: {
          currentSlot,
          onClickResetSlot: this.onClickResetSlot,
          handleTypeClick: this.onTypeClick,
          activeItem: selectedLicenseType,
        },
        [USER_INFO]: {
          examCenter,
          bookSlot: this.bookSlot,
          currentSlot,
          selectedLicenseType,
          onEditLicenseClick: this.onEditLicenseClick,
        },
        [REGISTRATION_TABS]: {
          onExamSigninFormSubmitHandler: this.onExamSigninFormSubmitHandler,
          onExamRegFormSubmitHandler: this.onExamRegFormSubmitHandler,
          examCenter,
          currentSlot,
          selectedLicenseType,
          onEditLicenseClick: this.onEditLicenseClick,
        },
        [BOOKING_ERROR]: {
          onClose: () => {
            this.setState({
              currentSlot: null,
              selectedLicenseType: null,
            });
            stores.bookingStore.activeScreen = SLOTS;
          },
          text: errorText,
        },
      };
      return SCREENS_PROPS[activeScreen];
    };

    const screenProps = getScreenProps();

    return (
      <>
        {activeScreen !== BOOKING_ERROR && (
          <Header
            averageStars={averageStars}
            reviewsCount={reviewsCount}
            examCenterType={examCenter}
          />
        )}
        <Screen {...screenProps} />
      </>
    );
  }
}

BookingFormClassComponent.propTypes = {
  examCenter: PropTypes.string,
  examCenterTimezone: PropTypes.string,
  examCenterSiteId: PropTypes.string,
  reviewsCount: PropTypes.number,
  averageStars: PropTypes.number,
};

BookingFormClassComponent.defaultProps = {
  examCenter: '',
  examCenterSiteId: null,
  reviewsCount: 0,
  averageStars: 0,
  examCenterTimezone: DEFAULT_TIME_ZONE_NAME,
};

const BookingForm = observer(BookingFormClassComponent);

export { BookingForm };
